export type ValidationRule<T = string> = {
  validate: (value: T) => boolean;
  message: string;
};

export type FieldValidator<T = string> = {
  rules: ValidationRule<T>[];
};

export type ValidationResult = {
  isValid: boolean;
  errorMessage: string | null;
};

export const validateField = <T>(
  value: any,
  validator?: FieldValidator<T>
): ValidationResult => {
  if (!validator || validator.rules.length === 0) {
    return { isValid: true, errorMessage: null };
  }

  for (const rule of validator.rules) {
    if (!rule.validate(value)) {
      return { isValid: false, errorMessage: rule.message };
    }
  }

  return { isValid: true, errorMessage: null };
};

// Common validation rules
export const validationRules = {
  required: (fieldName: string): ValidationRule<string | null> => ({
    validate: (value) =>
      value !== null && value !== undefined && value.trim() !== '',
    message: `${fieldName} is required`,
  }),

  minLength: (min: number, fieldName: string): ValidationRule => ({
    validate: (value) => value.length >= min,
    message: `${fieldName} must be at least ${min} characters`,
  }),

  maxLength: (max: number, fieldName: string): ValidationRule => ({
    validate: (value) => value.length <= max,
    message: `${fieldName} must be no more than ${max} characters`,
  }),

  pattern: (
    regex: RegExp,
    fieldName: string,
    patternName: string
  ): ValidationRule => ({
    validate: (value) => regex.test(value),
    message: `${fieldName} must match ${patternName} format`,
  }),

  email: (fieldName: string): ValidationRule => ({
    validate: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    message: `${fieldName} must be a valid email address`,
  }),

  min: (min: number, fieldName: string): ValidationRule<number | null> => ({
    validate: (value) => value !== null && value >= min,
    message: `${fieldName} must be at least ${min}`,
  }),

  max: (max: number, fieldName: string): ValidationRule<number | null> => ({
    validate: (value) => value !== null && value <= max,
    message: `${fieldName} must be no more than ${max}`,
  }),

  positive: (fieldName: string): ValidationRule<number | null> => ({
    validate: (value) => value !== null && value > 0,
    message: `${fieldName} must be a positive number`,
  }),

  nonNegative: (fieldName: string): ValidationRule<number | null> => ({
    validate: (value) => value !== null && value >= 0,
    message: `${fieldName} must be a non-negative number`,
  }),

  custom: <T>(
    validate: (value: T) => boolean,
    message: string
  ): ValidationRule<T> => ({
    validate,
    message,
  }),
};
